import React, { useState } from "react";
import { Form, Input, Button, Alert, Divider } from "antd";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { LOGIN, LOGIN_TELEGRAM } from "../graphql/queries";
import { TelegramAuthUser } from "../components/TelegramLoginWidget";
import { LoginResponse, TelegramLoginInput } from "../types";
import { useAppSettings } from "../context/AppSettingsContext";
import TelegramLoginWidget from "../components/TelegramLoginWidget";
import { getTelegramBotUsername } from "../utils/appSettings";

interface LoginProps {
  onLoginSuccess?: (userId: string) => void;
}

export default function Login({ onLoginSuccess }: LoginProps) {
  const [form] = Form.useForm();
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { appConfig } = useAppSettings();
  const telegramBotUsername = getTelegramBotUsername(appConfig);
  const [telegramWidgetReady, setTelegramWidgetReady] = useState<boolean | null>(null);
  const loginPageImageUrl =
    typeof appConfig?.loginPage === "string"
      ? appConfig.loginPage
      : typeof appConfig?.logo === "string"
        ? appConfig.logo
        : null;
  const appTitle = typeof appConfig?.appName === "string" ? appConfig.appName : "Spotlight";
  const [loginMutation, { loading }] = useMutation<{ login: LoginResponse }>(LOGIN);
  const [loginTelegramMutation, { loading: telegramMutationLoading }] =
    useMutation<{ loginTelegram: LoginResponse }>(LOGIN_TELEGRAM);

  const handleTelegramAuth = async (user: TelegramAuthUser) => {
    setError("");
    try {
      const input: TelegramLoginInput = { id: String(user.id) };
      if (user.first_name) input.first_name = user.first_name;
      if (user.last_name) input.last_name = user.last_name;
      if (user.username) input.username = user.username;
      if (user.photo_url) input.photo_url = user.photo_url;
      const { data } = await loginTelegramMutation({
        variables: { input },
      });

      if (data?.loginTelegram?.success && data.loginTelegram.user) {
        localStorage.setItem("userId", data.loginTelegram.user.id);
        localStorage.setItem("userName", data.loginTelegram.user.name || "");
        localStorage.setItem("userLoginName", data.loginTelegram.user.login_name || "");
        localStorage.setItem("telegramId", String(user.id));
        onLoginSuccess?.(data.loginTelegram.user.id);
        navigate("/");
      } else {
        setError(data?.loginTelegram?.message || "Přihlášení přes Telegram se nezdařilo.");
      }
    } catch (err) {
      console.error("Telegram login error:", err);
      setError("Došlo k chybě při komunikaci se serverem.");
    }
  };

  const handleFinish = async (values: { username: string; password: string }) => {
    setError("");

    try {
      const { data } = await loginMutation({
        variables: {
          input: {
            loginName: values.username.trim(),
            loginPassword: values.password.trim(),
          },
        },
      });

      if (data?.login?.success && data.login.user) {
        localStorage.removeItem("telegramId");
        localStorage.setItem("userId", data.login.user.id);
        localStorage.setItem("userName", data.login.user.name || '');
        localStorage.setItem("userLoginName", data.login.user.login_name || "");
        onLoginSuccess?.(data.login.user.id);
        navigate("/");
      } else {
        setError(data?.login?.message || "Nesprávné jméno nebo heslo.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Došlo k chybě při komunikaci se serverem.");
    }
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <div style={{ maxWidth: 400, width: "100%", padding: 24 }}>
        {loginPageImageUrl ? (
          <img src={loginPageImageUrl} alt="logo" style={{ width: "100%", maxHeight: 200, objectFit: "contain" }} />
        ) : (
          <h1 style={{ textAlign: "center", marginBottom: 0 }}>{appTitle}</h1>
        )}
        <h1 style={{ textAlign: "center" }}>Přihlášení</h1>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Uživatelské jméno"
            name="username"
            rules={[{ required: true, message: "Zadejte prosím své jméno." }]}
          >
            <Input placeholder="Zadejte své jméno" />
          </Form.Item>

          <Form.Item
            label="Heslo"
            name="password"
            rules={[{ required: true, message: "Zadejte prosím heslo." }]}
          >
            <Input.Password placeholder="Zadejte své heslo" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading || telegramMutationLoading}
              disabled={telegramMutationLoading}
              block
            >
              Pokračovat
            </Button>
          </Form.Item>
        </Form>

        {telegramBotUsername && telegramWidgetReady !== false && (
          <>
            {telegramWidgetReady === true && <Divider plain>nebo</Divider>}
            <TelegramLoginWidget
              botUsername={telegramBotUsername}
              onAuth={handleTelegramAuth}
              onAvailabilityChange={setTelegramWidgetReady}
            />
          </>
        )}

        {error && (
          <Alert message={error} type="error" style={{ marginTop: 16 }} />
        )}
      </div>
    </div>
  );
}
